import { Table, type TableColumn } from '@ctw/shared/components/table/table';
import { uniqueIdentifierForNode } from '@ctw/shared/content/document-viewer/_internal/renderers/helpers';
import { DOCUMENT_VIEWER_TABLE_TEST_ID } from '@ctw/shared/test/ids';
import { tw } from '@ctw/shared/utils/tailwind';
import { trim } from 'lodash-es';
import type { CdaSection } from '../../parsers/types';
import { ReactNodeRenderer } from './react-node-renderer';

interface TableRendererProps {
  id: string;
  section: CdaSection;
  table: HTMLTableElement;
}

interface TableRecord {
  key: string;
  tdElements: Array<HTMLTableCellElement>;
}

export const ReactTableRenderer = ({ id, section, table }: TableRendererProps) => {
  const [columns, records, rowHeaders] = tableDataFromHtml(section, table);

  return (
    <Table
      testId={DOCUMENT_VIEWER_TABLE_TEST_ID}
      id={id}
      title={`Table in ${section.title}`}
      titleVisuallyHidden={true}
      className={tw`!border w-full max-w-full rounded-md border-background-subtle`}
      records={records}
      columns={columns}
      rowHeaders={rowHeaders}
      emptyMessage="No data available"
    />
  );
};

const tableDataFromHtml = (
  section: CdaSection,
  table: HTMLTableElement,
): [Array<TableColumn<TableRecord>>, Array<TableRecord>, Array<string> | undefined] => {
  const columnThElements = table.querySelector('thead')
    ? table.querySelectorAll('thead tr:first-of-type:not(:has(td)) th')
    : table.querySelectorAll('tr:first-of-type:not(:has(td)) th');

  const rowThElements = table.querySelector('tbody')
    ? table.querySelectorAll('tbody tr:has(td) th:first-of-type')
    : table.querySelectorAll('tr:has(td) th:first-of-type');

  const recordTrElements = table.querySelector('tbody')
    ? table.querySelectorAll('tbody tr:has(td)')
    : table.querySelectorAll('tr:has(td)');

  const rowHeaders = Array.from(rowThElements).map((th) => trim(th.textContent ?? ''));

  const columns: Array<TableColumn<TableRecord>> = Array.from(columnThElements).map(
    (th, index) => ({
      title: trim(th.textContent ?? ''),
      headerClassName: 'whitespace-nowrap',
      render: (record) => {
        const td = record.tdElements.at(index);

        return (
          <>
            {Array.from(td?.childNodes ?? []).map((child) => (
              <ReactNodeRenderer
                id={uniqueIdentifierForNode({ section, node: child, context: 'table-cell-child' })}
                key={uniqueIdentifierForNode({ section, node: child, context: 'table-cell-child' })}
                section={section}
                node={child}
              />
            ))}
          </>
        );
      },
    }),
  );

  const records: Array<TableRecord> = Array.from(recordTrElements).map((tr) => ({
    key: uniqueIdentifierForNode({ section, node: tr, context: 'table-row' }),
    tdElements: Array.from(tr.querySelectorAll('td')),
  }));

  return [columns, records, rowHeaders];
};
